import { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import LinearProgress from "@mui/material/LinearProgress";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import RecordTable from "components/RecordTable";

import { adminApi } from "services/api";
import { sendOutcome } from "./sendOutcome";
import { courseLabel, COURSE_OPTIONS } from "utils/courses";
import PaymentLinkModal from "components/PaymentLinkModal";
import CallButton from "components/CallButton";
import { landingApplicationsToRows } from "utils/exportUtils";
import { lightDark, DARK } from "utils/lightDark";

const STATUS_COLORS = {
  booking: "info",
  contacted: "warning",
  enrolled: "success",
  rejected: "error",
  pending: "default",
};

const STATUS_OPTIONS = ["booking", "contacted", "enrolled", "rejected", "pending"];
const STAGE_FILTER_OPTIONS = STATUS_OPTIONS.map((s) => ({
  value: s,
  label: s.charAt(0).toUpperCase() + s.slice(1),
}));
const EXPORT_CONFIG = {
  fetchAll: adminApi.exportLandingApplications,
  toRows: landingApplicationsToRows,
  filename: "landing-applications",
  title: "Landing Page Applications",
};

// Below md the table becomes stacked cards: give touch targets ~40px there, keeping desktop sizes.
const ROW_ACTION_SX = { width: { xs: 40, md: "auto" }, height: { xs: 40, md: "auto" } };
const SMALL_BUTTON_TAP_SX = { minHeight: { xs: "2.5rem", md: "2rem" } };

const CHANNEL_LABELS = { email: "email", whatsapp: "WhatsApp", sms: "SMS" };

// Channels of a sent message that did not go through (results come from the send endpoint).
const failedChannels = (m) =>
  Object.entries(m.results || {})
    .filter(([, r]) => !r.success)
    .map(([channel]) => CHANNEL_LABELS[channel] || channel);

function LandingApplications() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [reloadToken, setReloadToken] = useState(0);

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Message dialog
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgTarget, setMsgTarget] = useState(null);
  const [channels, setChannels] = useState(["email"]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [msgError, setMsgError] = useState("");
  const [msgSuccess, setMsgSuccess] = useState("");

  // Status update
  const [statusUpdating, setStatusUpdating] = useState(null);
  const [statusError, setStatusError] = useState("");

  // Payment link modal
  const [paymentLinkTarget, setPaymentLinkTarget] = useState(null);

  const handleViewDetails = async (app) => {
    setSelected(app);
    setMessages([]);
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const data = await adminApi.getLandingApplication(app._id);
      setSelected(data.application);
      setMessages(data.messages || []);
    } catch {
      // keep initial data
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOpenMessage = (app, e) => {
    e.stopPropagation();
    setMsgTarget(app);
    setChannels(["email"]);
    setSubject("");
    setMessage("");
    setMsgError("");
    setMsgSuccess("");
    setMsgOpen(true);
  };

  const handleChannelToggle = (_, next) => {
    if (next.length > 0) setChannels(next);
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      setMsgError("Message is required");
      return;
    }
    if (channels.includes("email") && !subject.trim()) {
      setMsgError("Subject is required when sending via email");
      return;
    }
    try {
      setSending(true);
      setMsgError("");
      const result = await adminApi.sendMessageToApplicant(msgTarget._id, {
        subject,
        message,
        channels,
      });
      const outcome = sendOutcome(result, channels, msgTarget.email);
      if (outcome.error) setMsgError(outcome.error);
      if (outcome.success) setMsgSuccess(outcome.success);
      // Nothing went out: keep the draft so it can be fixed and sent again.
      if (outcome.clearDraft) {
        setSubject("");
        setMessage("");
      }
      // Refresh messages if detail dialog is open for this applicant
      // (a failed refresh must not look like a failed send)
      if (selected && selected._id === msgTarget._id) {
        try {
          const data = await adminApi.getLandingApplication(msgTarget._id);
          setMessages(data.messages || []);
        } catch {
          // the message went out; the history just stays as it was
        }
      }
    } catch (err) {
      setMsgError(
        err.response?.data?.error || err.response?.data?.warning || "Failed to send message"
      );
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (app, newStatus) => {
    try {
      setStatusUpdating(app._id);
      setStatusError("");
      await adminApi.updateApplicationStatus(app._id, newStatus);
      setReloadToken((t) => t + 1);
      if (selected && selected._id === app._id) {
        setSelected((s) => ({ ...s, stage: newStatus }));
      }
    } catch (err) {
      // The dropdown is driven by the row's saved stage, so it snaps back on its own.
      setStatusError(
        err.response?.data?.error || "Could not change the status. It was left as it was."
      );
    } finally {
      setStatusUpdating(null);
    }
  };

  const columns = [
    { Header: "name", accessor: "name", width: "15%", align: "left" },
    { Header: "email", accessor: "email", width: "19%", align: "left" },
    { Header: "phone", accessor: "phone", width: "12%", align: "left" },
    { Header: "region", accessor: "region", width: "8%", align: "left" },
    { Header: "course", accessor: "course", width: "14%", align: "left" },
    { Header: "qualified", accessor: "qualified", width: "9%", align: "center" },
    { Header: "status", accessor: "status", width: "12%", align: "center" },
    { Header: "date", accessor: "date", width: "9%", align: "left" },
    { Header: "actions", accessor: "actions", width: "12%", align: "center" },
  ];

  const renderRow = (app) => ({
    name: (
      <MDTypography
        variant="button"
        fontWeight="medium"
        sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
        onClick={() => handleViewDetails(app)}
      >
        {app.name}
      </MDTypography>
    ),
    email: <MDTypography variant="caption">{app.email}</MDTypography>,
    phone: (
      <MDBox display="flex" alignItems="center" gap={0.5}>
        <MDTypography variant="caption">{app.phone || "—"}</MDTypography>
        <CallButton phoneNumber={app.phone} />
      </MDBox>
    ),
    region: <MDTypography variant="caption">{app.region || "—"}</MDTypography>,
    course: app.courseSlug ? (
      <Chip label={courseLabel(app.courseSlug)} size="small" color="secondary" variant="outlined" />
    ) : (
      <MDTypography variant="caption">—</MDTypography>
    ),
    qualified: (
      <Chip
        label={app.qualified === true ? "Yes" : app.qualified === false ? "No" : "—"}
        color={app.qualified === true ? "success" : app.qualified === false ? "error" : "default"}
        size="small"
      />
    ),
    status: (
      <TextField
        select
        size="small"
        value={app.stage || "booking"}
        disabled={statusUpdating === app._id}
        onChange={(e) => handleStatusChange(app, e.target.value)}
        sx={{ minWidth: 110, width: { xs: "100%", md: "auto" } }}
        onClick={(e) => e.stopPropagation()}
      >
        {STATUS_OPTIONS.map((s) => (
          <MenuItem key={s} value={s}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </MenuItem>
        ))}
      </TextField>
    ),
    date: (
      <MDTypography variant="caption">
        {app.submittedAt ? new Date(app.submittedAt).toLocaleDateString() : "—"}
      </MDTypography>
    ),
    actions: (
      <MDBox display="flex" gap={0.5} justifyContent="center">
        <Tooltip title="View details">
          <IconButton size="small" sx={ROW_ACTION_SX} onClick={() => handleViewDetails(app)}>
            <Icon fontSize="small">visibility</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip title="Send message">
          <IconButton
            size="small"
            color="primary"
            sx={ROW_ACTION_SX}
            onClick={(e) => handleOpenMessage(app, e)}
          >
            <Icon fontSize="small">send</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip title="Create payment link">
          <IconButton
            size="small"
            onClick={() => setPaymentLinkTarget(app)}
            sx={{ ...ROW_ACTION_SX, color: "#00996a" }}
          >
            <Icon fontSize="small">link</Icon>
          </IconButton>
        </Tooltip>
      </MDBox>
    ),
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1.5}
              >
                <MDBox>
                  <MDTypography variant="h6" color="white">
                    Landing Page Applications
                  </MDTypography>
                  <MDTypography variant="caption" color="white">
                    Every application from the landing pages
                  </MDTypography>
                </MDBox>
              </MDBox>

              {statusError && (
                <MDBox px={2} pt={2}>
                  <MDAlert color="error" dismissible onClose={() => setStatusError("")}>
                    {statusError}
                  </MDAlert>
                </MDBox>
              )}

              <RecordTable
                columns={columns}
                fetchPage={adminApi.getLandingApplications}
                renderRow={renderRow}
                statusOptions={STAGE_FILTER_OPTIONS}
                courseOptions={COURSE_OPTIONS}
                reloadToken={reloadToken}
                exportConfig={EXPORT_CONFIG}
                emptyMessage="No applications yet"
              />
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* ── Detail Dialog ── */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
      >
        <DialogTitle>
          Applicant Details
          {selected && (
            <Chip
              label={selected.stage || "booking"}
              color={STATUS_COLORS[selected.stage] || "default"}
              size="small"
              sx={{ ml: 2, verticalAlign: "middle" }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {detailLoading && (
            <MDTypography variant="body2" mb={1}>
              Loading...
            </MDTypography>
          )}
          {selected && (
            <>
              {[
                { label: "Name", value: selected.name },
                { label: "Email", value: selected.email },
                { label: "Phone", value: selected.phone },
                { label: "City", value: selected.city },
                { label: "Region", value: selected.region },
                { label: "Course", value: courseLabel(selected.courseSlug) },
                { label: "Ecosystem", value: selected.ecosystem },
                { label: "Payment Preference", value: selected.paymentPreference },
                {
                  label: "Qualified",
                  value:
                    selected.qualified === true
                      ? "Yes"
                      : selected.qualified === false
                      ? "No"
                      : "N/A",
                },
                {
                  label: "Submitted",
                  value: selected.submittedAt
                    ? new Date(selected.submittedAt).toLocaleString()
                    : null,
                },
              ]
                .filter((f) => f.value)
                .map((f) => (
                  <MDBox key={f.label} mb={1.5}>
                    <MDTypography variant="button" fontWeight="bold">
                      {f.label}:
                    </MDTypography>{" "}
                    <MDTypography variant="body2" component="span">
                      {f.value}
                    </MDTypography>
                  </MDBox>
                ))}

              {selected.qualification != null &&
                (() => {
                  const q = selected.qualification;
                  const pct = q.maxScore > 0 ? Math.round((q.totalScore / q.maxScore) * 100) : 0;
                  const color = pct >= 70 ? "success" : pct >= 45 ? "warning" : "error";
                  return (
                    <MDBox mt={1} mb={1.5}>
                      <MDTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
                        Qualification Score
                      </MDTypography>
                      <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                        <MDBox flex={1}>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            color={color}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </MDBox>
                        <MDTypography variant="caption" fontWeight="bold" sx={{ minWidth: 60 }}>
                          {q.totalScore} / {q.maxScore} ({pct}%)
                        </MDTypography>
                        <Chip
                          label={q.qualified ? "Passed" : "Failed"}
                          color={q.qualified ? "success" : "error"}
                          size="small"
                        />
                      </MDBox>
                      {Array.isArray(q.answers) && q.answers.length > 0 && (
                        <MDBox mt={1}>
                          {q.answers.map((a, i) => (
                            <MDBox
                              key={i}
                              mb={1}
                              p={1}
                              sx={{
                                background: lightDark("#f9f9f9", DARK.surface),
                                borderRadius: 1,
                                borderLeft: `3px solid ${
                                  a.score >= 2 ? "#4caf50" : a.score === 1 ? "#ff9800" : "#f44336"
                                }`,
                              }}
                            >
                              <MDTypography variant="caption" color="secondary" display="block">
                                Q{i + 1}: {a.question}
                              </MDTypography>
                              <MDBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mt={0.25}
                              >
                                <MDTypography variant="caption" fontWeight="medium">
                                  {a.answer}
                                </MDTypography>
                                <Chip
                                  label={`${a.score} pt${a.score !== 1 ? "s" : ""}`}
                                  size="small"
                                  variant="outlined"
                                />
                              </MDBox>
                            </MDBox>
                          ))}
                        </MDBox>
                      )}
                    </MDBox>
                  );
                })()}

              {messages.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
                    Messages Sent ({messages.length})
                  </MDTypography>
                  {messages.map((m, i) => (
                    <MDBox
                      key={i}
                      mb={1.5}
                      p={1.5}
                      sx={{
                        background: lightDark("#f5f5f5", DARK.surface),
                        borderRadius: 1,
                        borderLeft: "3px solid #1976d2",
                      }}
                    >
                      <MDTypography variant="caption" fontWeight="bold">
                        {m.subject}
                      </MDTypography>
                      <MDTypography
                        variant="caption"
                        display="block"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {m.message}
                      </MDTypography>
                      <MDTypography variant="caption" color="secondary">
                        Sent {new Date(m.sentAt).toLocaleString()} · by {m.sentBy}
                        {failedChannels(m).length > 0 &&
                          ` · ⚠ delivery failed on: ${failedChannels(m).join(", ")}`}
                      </MDTypography>
                    </MDBox>
                  ))}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {selected && (
            <MDButton
              color="primary"
              size="small"
              startIcon={<Icon>send</Icon>}
              sx={SMALL_BUTTON_TAP_SX}
              onClick={(e) => {
                setDetailOpen(false);
                handleOpenMessage(selected, e);
              }}
            >
              Send Message
            </MDButton>
          )}
          <MDButton onClick={() => setDetailOpen(false)} color="secondary">
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── Send Message Dialog ── */}
      <Dialog open={msgOpen} onClose={() => setMsgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Send Message to {msgTarget?.name}
          <MDTypography variant="caption" display="block" color="secondary">
            {msgTarget?.email} · {msgTarget?.phone || "no phone"}
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          {msgError && (
            <MDBox mb={2}>
              <MDAlert color="error" dismissible onClose={() => setMsgError("")}>
                {msgError}
              </MDAlert>
            </MDBox>
          )}
          {msgSuccess && (
            <MDBox mb={2}>
              <MDAlert color="success" dismissible onClose={() => setMsgSuccess("")}>
                {msgSuccess}
              </MDAlert>
            </MDBox>
          )}

          <MDTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
            Send via
          </MDTypography>
          <ToggleButtonGroup
            value={channels}
            onChange={handleChannelToggle}
            size="small"
            fullWidth={isPhone}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="email">
              <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>email</Icon>
              Email
            </ToggleButton>
            <ToggleButton value="whatsapp">
              <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>chat</Icon>
              WhatsApp
            </ToggleButton>
            <ToggleButton value="sms">
              <Icon sx={{ mr: 0.5, fontSize: "1rem" }}>sms</Icon>
              SMS
            </ToggleButton>
          </ToggleButtonGroup>

          {channels.includes("email") && (
            <TextField
              label="Subject"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              sx={{ mb: 2 }}
              size="small"
            />
          )}
          <TextField
            label="Message"
            fullWidth
            multiline
            minRows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write your message to the applicant..."
          />
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setMsgOpen(false)} color="secondary" disabled={sending}>
            Cancel
          </MDButton>
          <MDButton
            onClick={handleSendMessage}
            color="primary"
            disabled={sending || !!msgSuccess}
            startIcon={<Icon>send</Icon>}
          >
            {sending ? "Sending..." : `Send via ${channels.join(" + ")}`}
          </MDButton>
        </DialogActions>
      </Dialog>

      <PaymentLinkModal
        open={!!paymentLinkTarget}
        onClose={() => setPaymentLinkTarget(null)}
        prefill={{
          name: paymentLinkTarget?.name || "",
          email: paymentLinkTarget?.email || "",
          phone: paymentLinkTarget?.phone || "",
          description: `Landing page enquiry – ${paymentLinkTarget?.name || ""}`,
        }}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default LandingApplications;
